import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
    CTableWrapper,
    DIdAutocomplete,
    ICustomTableWrapperProps,
    SortedOrder,
} from '../../../../dum';
import { useProductionPerformanceMachineProductSummaryLazyQuery } from '../../../../../services';
import { formatDate, formatFloat } from '../../../../../helpers';
import { ProductionPerformanceAnalysis } from '../production-performance-analysis';
import { usePerfFilters } from '../perf-filters-context';
import { SHIFT_HOURS } from '../../../../smart/production-performance';

const MIN_RUNS_FOR_INDEX = 3;

interface IMachinesTabProps {
    machines: {
        id: number;
        name: string;
        categoryName: string;
        order_production_type_id?: number | null;
    }[];
    products: { id: number; description: string; categoryName: string }[];
}

interface ProductRow {
    productId: number;
    productDescription: string;
    runs: number;
    kilos: number;
    hours: number;
    kgHr: number | null;
    // Bultos/hr and its 8 h-shift projection — the unit the floor plans in.
    bultosHr: number | null;
    bultosShift: number | null;
    pctOfTotal: number;
    mermaPct: number;
    lastRunDate: string | null;
    wasteShareTotal: number;
}

export function MachinesTab({ machines, products }: IMachinesTabProps) {
    const { fromDate, toDate, singleProductOnly } = usePerfFilters();
    const [machineId, setMachineId] = useState<number | null>(null);
    // Product sub-filter that flows into the analysis panel below the table.
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    // Product overview table sort.
    const [sortedIndex, setSortedIndex] = useState<number | null>(null);
    const [sortedOrder, setSortedOrder] = useState<SortedOrder>(null);

    const [querySummary, { data, loading }] =
        useProductionPerformanceMachineProductSummaryLazyQuery();

    // Refetch on machine or window change. The product sub-filter is reset only
    // when the MACHINE changes (in the autocomplete's onChange), never here — so
    // moving Desde/Hasta keeps the selected product instead of clearing it.
    useEffect(() => {
        if (machineId) {
            void querySummary({
                variables: {
                    machineId,
                    fromDate: fromDate || null,
                    toDate: toDate || null,
                },
            });
        }
    }, [machineId, fromDate, toDate, querySummary]);

    // Product sub-filter options — reuse the overview rows already fetched.
    const productOptions = useMemo(() => {
        const categoryNames = new Map(
            products.map((product) => [product.id, product.categoryName]),
        );
        return (data?.getMachineProductPerformanceSummary ?? [])
            .map((r) => ({
                id: r.product_id,
                description: r.product_description,
                categoryName:
                    categoryNames.get(r.product_id) ?? 'Sin categoría',
            }))
            .sort((a, b) =>
                a.categoryName.localeCompare(b.categoryName, 'es', {
                    sensitivity: 'base',
                }) ||
                a.description.localeCompare(b.description, 'es', {
                    sensitivity: 'base',
                }),
            );
    }, [data, products]);

    const rows = useMemo<ProductRow[]>(() => {
        const raw = data?.getMachineProductPerformanceSummary ?? [];
        const totalKilos = raw.reduce((s, r) => s + r.kilos, 0);
        const mapped = raw.map((r) => {
            const kgHr = r.hours > 0 ? r.kilos / r.hours : null;
            const bultosHr = r.hours > 0 ? r.groups / r.hours : null;
            return {
                productId: r.product_id,
                productDescription: r.product_description,
                runs: r.runs,
                kilos: r.kilos,
                hours: r.hours,
                kgHr,
                bultosHr,
                bultosShift: bultosHr === null ? null : bultosHr * SHIFT_HOURS,
                pctOfTotal: totalKilos > 0 ? (r.kilos / totalKilos) * 100 : 0,
                mermaPct: r.kilos > 0 ? (r.waste_share_total / r.kilos) * 100 : 0,
                lastRunDate: r.last_run_date
                    ? formatDate(r.last_run_date)
                    : null,
                wasteShareTotal: r.waste_share_total,
            };
        });
        // Column index → sort key mapping (must match tableItems order below).
        // 0=Producto(text), 1=Corridas, 2=Kilos, 3=Horas, 4=kg/hr, 5=Bultos/hr,
        // 6=Turno(8h), 7=%total, 8=Merma%, 9=Última corrida
        if (sortedIndex !== null && sortedOrder !== null) {
            mapped.sort((a, b) => {
                let av: number | string | null;
                let bv: number | string | null;
                switch (sortedIndex) {
                    case 0: av = a.productDescription; bv = b.productDescription; break;
                    case 1: av = a.runs; bv = b.runs; break;
                    case 2: av = a.kilos; bv = b.kilos; break;
                    case 3: av = a.hours; bv = b.hours; break;
                    case 4: av = a.kgHr; bv = b.kgHr; break;
                    case 5: case 6: av = a.bultosHr; bv = b.bultosHr; break;
                    case 7: av = a.pctOfTotal; bv = b.pctOfTotal; break;
                    case 8: av = a.mermaPct; bv = b.mermaPct; break;
                    case 9: av = a.lastRunDate; bv = b.lastRunDate; break;
                    default: return 0;
                }
                if (av === null && bv === null) return 0;
                if (av === null) return 1;
                if (bv === null) return -1;
                const cmp = typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number);
                return sortedOrder === 'asc' ? cmp : -cmp;
            });
        }
        return mapped;
    }, [data, sortedIndex, sortedOrder]);

    const tableItems = useMemo<ICustomTableWrapperProps<ProductRow>['items']>(
        () => [
            {
                title: 'Producto',
                textAlign: 'left',
                cellGrow: 3,
                sortable: true,
                content: (r) => r.productDescription,
            },
            {
                title: 'Corridas',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.runs),
            },
            {
                title: 'Kilos',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.kilos),
            },
            {
                title: 'Horas',
                textAlign: 'right',
                sortable: true,
                content: (r) => formatFloat(r.hours),
            },
            {
                title: 'kg/hr',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.kgHr === null || r.runs < MIN_RUNS_FOR_INDEX
                        ? '—'
                        : formatFloat(r.kgHr),
            },
            {
                title: 'Bultos/hr',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.bultosHr === null || r.runs < MIN_RUNS_FOR_INDEX
                        ? '—'
                        : formatFloat(r.bultosHr),
            },
            {
                title: 'Turno (8 h)',
                textAlign: 'right',
                sortable: true,
                content: (r) =>
                    r.bultosShift === null || r.runs < MIN_RUNS_FOR_INDEX
                        ? '—'
                        : formatFloat(r.bultosShift),
            },
            {
                title: '% del total',
                textAlign: 'right',
                sortable: true,
                content: (r) => `${formatFloat(r.pctOfTotal)}%`,
            },
            {
                title: 'Merma %',
                textAlign: 'right',
                sortable: true,
                content: (r) => `${formatFloat(r.mermaPct)}%`,
            },
            {
                title: 'Última corrida',
                textAlign: 'left',
                sortable: true,
                content: (r) => r.lastRunDate ?? '—',
            },
        ],
        [],
    );

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ minWidth: 220 }}>
                    <DIdAutocomplete
                        id={machineId}
                        onChange={(id) => {
                            setMachineId(id);
                            setSelectedProductId(null);
                        }}
                        itemText={'name'}
                        label={'Máquina'}
                        options={machines}
                        groupBy={(machine) => machine.categoryName}
                    />
                </Box>
                {machineId ? (
                    <Box sx={{ minWidth: 260 }}>
                        <DIdAutocomplete
                            id={selectedProductId}
                            onChange={setSelectedProductId}
                            itemText={'description'}
                            label={'Producto (opcional)'}
                            options={productOptions}
                            groupBy={(product) => product.categoryName}
                        />
                    </Box>
                ) : null}
            </Box>

            {!machineId ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant={'body1'} sx={{ color: 'text.secondary' }}>
                        Selecciona una máquina para ver su rendimiento por producto.
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* flexShrink: 0 — the tab lives in a bounded flex column
                        (DTabs → TabPanel). TableContainer's overflow makes its
                        flex min-height resolve to 0, so without this the table is
                        the only shrinkable child and collapses to a sliver; here
                        it keeps its natural height and the tab body scrolls. */}
                    <Box sx={{ flexShrink: 0 }}>
                        <CTableWrapper
                            items={tableItems}
                            data={rows}
                            loading={loading}
                            sortedIndex={sortedIndex}
                            setSortedIndex={setSortedIndex}
                            sortedOrder={sortedOrder}
                            setSortedOrder={setSortedOrder}
                            onRowDoubleClick={(r) =>
                                setSelectedProductId(
                                    r.productId === selectedProductId
                                        ? null
                                        : r.productId,
                                )
                            }
                            rowSx={(r) =>
                                r.productId === selectedProductId
                                    ? { backgroundColor: 'action.selected' }
                                    : {}
                            }
                        />
                    </Box>

                    <Box sx={{ mt: 3, flexShrink: 0 }}>
                        <ProductionPerformanceAnalysis
                            machineId={machineId}
                            productId={selectedProductId}
                            fromDate={fromDate}
                            toDate={toDate}
                            singleProductOnly={singleProductOnly}
                        />
                    </Box>
                </>
            )}
        </>
    );
}
